import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import type { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import { EntityMemberPageArgs, useLazyGetEntityMemberPageQuery } from "_shared/entity-members/_api/entity-member-api";
import EntityMemberQuery from "_shared/entity-members/_api/queries/entity-member-query";
import { EntityMemberSummaryJSON } from "_shared/entity-members/entity-member-summary-schema";
import type Guid from "_shared/guids/guid";
import { useEffect, useState } from "react";

export const ROSTER_PAGE_SIZE = 100;
export const MAX_ROSTER_PAGES = 50;

type RosterPageFetch = (args: EntityMemberPageArgs) => Promise<Paginated<EntityMemberSummaryJSON>>;

type RosterState = {
  legalEntityId?: string;
  members?: EntityMemberSummaryJSON[];
  error?: unknown;
};

/**
 * Every member of the firm, read a page at a time until the last.
 *
 * Interim, for two readers that search in the browser. The client selector merges the firm's
 * members with the caller's network connections, and #5434 converts it to scroll once both lists
 * page on the server. The team leader handover's successor table (#5836) filters on signing
 * authority, which the members endpoint cannot. Until each moves to the server, neither may see
 * only the first page of members, which is all an unchanged read of the paged endpoint returns
 * (#5442).
 *
 * `fresh` asks the server rather than the cache, for a reader that decides something from a
 * member's state: the handover refuses an offer on the roster's credentials, and a profile edit does
 * not invalidate the members it was cached with (#6095). `refetch` asks the server again; the last
 * answer stays on show until the new one arrives.
 */
export default function useWholeFirmRoster(
  legalEntityId: Guid | undefined,
  options: { skip: boolean; fresh?: boolean },
) {
  const [trigger] = useLazyGetEntityMemberPageQuery();
  const [state, setState] = useState<RosterState>({});
  const [generation, setGeneration] = useState(0);
  const entityId = options.skip ? undefined : legalEntityId?.value;
  const preferCacheValue = options.fresh !== true && generation === 0;

  useEffect(() => {
    if (!entityId) return;
    let cancelled = false;
    readWholeRoster((args) => trigger(args, preferCacheValue).unwrap(), entityId).then(
      (members) => {
        if (!cancelled) setState({ legalEntityId: entityId, members });
      },
      (error: unknown) => {
        if (!cancelled) setState({ legalEntityId: entityId, error });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [entityId, trigger, preferCacheValue, generation]);

  const isCurrent = entityId !== undefined && state.legalEntityId === entityId;
  return {
    data: isCurrent ? state.members : undefined,
    error: isCurrent ? state.error : undefined,
    isLoading: entityId !== undefined && !isCurrent,
    refetch: () => setGeneration((previous) => previous + 1),
  };
}

/** Module-level so that its loop stays out of the component the React Compiler lowers. */
export async function readWholeRoster(fetchPage: RosterPageFetch, legalEntityId: string): Promise<EntityMemberSummaryJSON[]> {
  const members: EntityMemberSummaryJSON[] = [];
  for (let pageIndex = 0; pageIndex < MAX_ROSTER_PAGES; pageIndex += 1) {
    const page = await fetchPage({
      legalEntityId,
      query: new EntityMemberQuery({
        paginationParameters: PaginationParameters.create({ pageIndex, pageSize: ROSTER_PAGE_SIZE }),
      }),
    });
    members.push(...page.data);
    const isLastPage = page.data.length < ROSTER_PAGE_SIZE || members.length >= page.headers.totalElements;
    if (isLastPage) break;
  }
  return members;
}
